import { useState, useMemo } from 'react';
import {
  useLayers,
  useCreateLayer,
  useUpdateLayer,
  useDeleteLayer,
  useParameters,
  useBulkCreateLayerMapping,
} from '@/features/deploymentSettings/hooks/useDeploymentSettings';
import { BulkUploadModal } from '@/shared/ui/BulkUploadModal';
import { useApiErrorToast } from '@/shared/ui/Toast';
import { SkeletonCard } from '@/shared/ui/SkeletonCard';
import { PrimaryButton } from '@/shared/ui/PrimaryButton';
import { UnsavedBar } from '@/shared/ui/UnsavedBar';
import { ModalShell } from '@/shared/ui/ModalShell';
import { useThemeColors, useIsDark } from '@/shared/theme/useThemeColors';
import { layerColors, paramTypeMeta, semantic, typography, radius } from '@/shared/theme/tokens';

function generateId() {
  return `layer-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
}


// ── Section 1: Layer Management ───────────────────────────────────────────────
const DND_LAYER = 'application/x-layer-id';

function LayerManagementSection({ layers, onAdd, onRename, onDelete, onReorder, canManage }) {
  const c = useThemeColors();
  const isDark = useIsDark();
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState('');
  const [dragOver, setDragOver] = useState(null);
  const [draggingId, setDraggingId] = useState(null);

  function startRename(layer, e) {
    e.stopPropagation();
    setEditingId(layer.id);
    setEditName(layer.name);
  }

  function commitRename(layer) {
    const trimmed = editName.trim();
    if (trimmed && trimmed !== layer.name) onRename(layer, trimmed);
    setEditingId(null);
  }

  function handleDragStart(e, layerId) {
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData(DND_LAYER, layerId);
    setDraggingId(layerId);
  }

  function handleDrop(e, targetId) {
    e.preventDefault();
    const srcId = e.dataTransfer.getData(DND_LAYER);
    setDragOver(null);
    setDraggingId(null);
    if (srcId && srcId !== targetId) onReorder(srcId, targetId);
  }

  return (
    <div style={{
      background: c.bg,
      border: `1px solid ${c.border}`,
      borderRadius: radius.xl,
      overflow: 'hidden',
      marginBottom: '16px',
      boxShadow: isDark ? 'none' : '0 2px 8px rgba(30,67,150,0.04)',
    }}>
      {/* Header */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '12px 16px',
        background: c.bgSubtle,
        borderBottom: `1px solid ${c.border}`,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span className="material-symbols-outlined" style={{ fontSize: 16, color: c.textSubtle, fontVariationSettings: "'FILL' 1" }}>layers</span>
          <span style={{ fontSize: typography.fontSize.md, fontWeight: typography.fontWeight.bold, color: c.textPrimary, fontFamily: typography.fontFamily.heading }}>
            Layers
          </span>
          <span style={{
            fontSize: typography.fontSize.xs, fontWeight: typography.fontWeight.bold,
            background: c.borderSubtle,
            color: c.textSubtle, padding: '1px 7px', borderRadius: radius.lg,
          }}>{layers.length}</span>
        </div>
        {canManage && (
          <PrimaryButton icon="add" size="sm" onClick={onAdd}>
            Add Layer
          </PrimaryButton>
        )}
      </div>

      {/* Layer rows */}
      {layers.length === 0 ? (
        <div style={{ padding: '40px', textAlign: 'center', color: c.textSubtle, fontSize: typography.fontSize.md }}>
          {canManage ? 'No layers yet. Click Add Layer to create one.' : 'No layers configured.'}
        </div>
      ) : (
        <div style={{ padding: '8px' }}>
          {layers.map((layer, idx) => {
            const dot = layerColors[idx % layerColors.length];
            const isEditing = editingId === layer.id;
            const isDragging = draggingId === layer.id;
            const isOver = dragOver === layer.id;

            return (
              <div
                key={layer.id}
                draggable={canManage && !isEditing}
                onDragStart={(e) => handleDragStart(e, layer.id)}
                onDragEnd={() => { setDraggingId(null); setDragOver(null); }}
                onDragOver={(e) => { e.preventDefault(); setDragOver(layer.id); }}
                onDragLeave={() => setDragOver(null)}
                onDrop={(e) => handleDrop(e, layer.id)}
                style={{
                  display: 'flex', alignItems: 'center', gap: '10px',
                  padding: '10px 12px', borderRadius: radius.lg, marginBottom: '4px',
                  background: isOver
                    ? (isDark ? 'rgba(62,181,231,0.08)' : 'rgba(30,67,150,0.05)')
                    : isDragging
                    ? (isDark ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)')
                    : 'transparent',
                  border: isOver
                    ? `1.5px dashed ${isDark ? 'rgba(62,181,231,0.4)' : 'rgba(30,67,150,0.3)'}`
                    : '1px solid transparent',
                  opacity: isDragging ? 0.45 : 1,
                  transition: 'all 0.12s',
                  cursor: canManage && !isEditing ? 'grab' : 'default',
                }}
              >
                {/* Order badge */}
                <div style={{
                  width: 22, height: 22, borderRadius: radius.sm, flexShrink: 0,
                  background: `${dot}18`, border: `1px solid ${dot}40`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: typography.fontSize.xs, fontWeight: typography.fontWeight.extrabold, color: dot,
                }}>
                  {idx + 1}
                </div>

                {/* Color dot */}
                <div style={{ width: 8, height: 8, borderRadius: '50%', background: dot, flexShrink: 0 }} />

                {/* Name */}
                {isEditing ? (
                  <input
                    autoFocus
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    onBlur={() => commitRename(layer)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') commitRename(layer);
                      if (e.key === 'Escape') setEditingId(null);
                    }}
                    style={{
                      flex: 1, fontSize: typography.fontSize.md,
                      background: c.bg,
                      color: c.textPrimary,
                      border: `1.5px solid ${isDark ? semantic.brandAccent : semantic.brandNavyMid}`,
                      borderRadius: radius.sm, padding: '3px 8px', outline: 'none',
                      fontFamily: typography.fontFamily.body,
                    }}
                  />
                ) : (
                  <span style={{ flex: 1, fontSize: typography.fontSize.md, fontWeight: typography.fontWeight.medium, color: c.textPrimary }}>
                    {layer.name}
                  </span>
                )}

                {/* Drag hint */}
                {canManage && !isEditing && (
                  <span className="material-symbols-outlined" style={{ fontSize: 14, color: c.borderSubtle, flexShrink: 0 }}>
                    drag_indicator
                  </span>
                )}

                {/* Actions */}
                {canManage && !isEditing && (
                  <div style={{ display: 'flex', gap: '4px', flexShrink: 0 }}>
                    <button onClick={(e) => startRename(layer, e)} style={iconBtn(c)} title="Rename">
                      <span className="material-symbols-outlined" style={{ fontSize: 14, color: c.textSubtle }}>edit</span>
                    </button>
                    <button onClick={() => onDelete(layer)} style={{ ...iconBtn(c), borderColor: semantic.errorBorder }} title="Delete">
                      <span className="material-symbols-outlined" style={{ fontSize: 14, color: semantic.errorAlt }}>delete</span>
                    </button>
                  </div>
                )}
              </div>
            );
          })}
          {canManage && (
            <p style={{ margin: '4px 10px 2px', fontSize: typography.fontSize.sm, color: c.borderSubtle }}>
              Drag rows to reorder
            </p>
          )}
        </div>
      )}
    </div>
  );
}

// ── Section 2: Parameter Assignment ──────────────────────────────────────────
const DND_PARAM = 'application/x-param-id';

function ParamChip({ param, layerColor: _layerColor, onRemove, canManage }) {
  const c = useThemeColors();
  const isDark = useIsDark();
  const [dragging, setDragging] = useState(false);
  const tm = paramTypeMeta[param.type] ?? paramTypeMeta.target;

  return (
    <div
      draggable={canManage}
      onDragStart={(e) => {
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData(DND_PARAM, param.id);
        setDragging(true);
      }}
      onDragEnd={() => setDragging(false)}
      style={{
        display: 'flex', alignItems: 'center', gap: '0',
        borderRadius: radius.md, overflow: 'hidden', marginBottom: '4px',
        border: dragging
          ? `1.5px dashed ${isDark ? semantic.brandAccent : semantic.brandNavyMid}`
          : `1px solid ${c.border}`,
        background: dragging
          ? (isDark ? 'rgba(62,181,231,0.06)' : semantic.infoBg)
          : (c.bg),
        opacity: dragging ? 0.5 : 1,
        cursor: canManage ? 'grab' : 'default',
        transition: 'all 0.12s',
        userSelect: 'none',
      }}
    >
      <div style={{ width: 3, alignSelf: 'stretch', background: tm.bar, flexShrink: 0 }} />
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '8px', padding: '7px 10px', minWidth: 0 }}>
        {canManage && (
          <span className="material-symbols-outlined" style={{ fontSize: 12, color: c.borderSubtle, flexShrink: 0 }}>drag_indicator</span>
        )}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: typography.fontSize.base, fontWeight: typography.fontWeight.semibold, color: c.textPrimary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {param.displayName}
          </div>
          <div style={{ fontSize: typography.fontSize.xs, color: c.textSubtle, fontFamily: typography.fontFamily.mono, marginTop: '1px' }}>
            {param.dataKey}
          </div>
        </div>
        <span style={{ padding: '1px 6px', borderRadius: radius.xs, fontSize: typography.fontSize.xs, fontWeight: typography.fontWeight.bold, background: tm.bg, color: tm.text, textTransform: 'uppercase', letterSpacing: typography.letterSpacing.label, flexShrink: 0 }}>
          {tm.label}
        </span>
        {canManage && (
          <button onClick={() => onRemove(param.id)} title="Remove from layer" style={{
            width: 20, height: 20, borderRadius: radius.sm, border: 'none', padding: 0,
            background: semantic.errorContainer, color: semantic.errorAlt,
            cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          }}>
            <span className="material-symbols-outlined" style={{ fontSize: 13 }}>chevron_right</span>
          </button>
        )}
      </div>
    </div>
  );
}

function AvailableChip({ param, alreadyAssignedTo, onAdd, canManage }) {
  const c = useThemeColors();
  const tm = paramTypeMeta[param.type] ?? paramTypeMeta.target;
  const locked = !!alreadyAssignedTo;

  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: '0',
      borderRadius: radius.md, overflow: 'hidden', marginBottom: '4px',
      border: `1px solid ${c.border}`,
      background: locked
        ? (c.bgSubtle)
        : (c.bg),
      opacity: locked ? 0.55 : 1,
      cursor: locked ? 'not-allowed' : (canManage ? 'grab' : 'default'),
      userSelect: 'none',
    }}
      draggable={canManage && !locked}
      onDragStart={(e) => {
        if (locked) { e.preventDefault(); return; }
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData(DND_PARAM, param.id);
      }}
    >
      <div style={{ width: 3, alignSelf: 'stretch', background: locked ? c.borderSubtle : tm.bar, flexShrink: 0 }} />
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '8px', padding: '7px 10px', minWidth: 0 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: typography.fontSize.base, fontWeight: typography.fontWeight.semibold, color: locked ? c.textSubtle : c.textPrimary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {param.displayName}
          </div>
          {locked ? (
            <div style={{ fontSize: typography.fontSize.xs, color: c.textSubtle, marginTop: '1px', display: 'flex', alignItems: 'center', gap: '3px' }}>
              <span className="material-symbols-outlined" style={{ fontSize: 10 }}>lock</span>
              {alreadyAssignedTo}
            </div>
          ) : (
            <div style={{ fontSize: typography.fontSize.xs, color: c.textSubtle, fontFamily: typography.fontFamily.mono, marginTop: '1px' }}>
              {param.dataKey}
            </div>
          )}
        </div>
        {!locked && (
          <span style={{ padding: '1px 6px', borderRadius: radius.xs, fontSize: typography.fontSize.xs, fontWeight: typography.fontWeight.bold, background: tm.bg, color: tm.text, textTransform: 'uppercase', letterSpacing: typography.letterSpacing.label, flexShrink: 0 }}>
            {tm.label}
          </span>
        )}
        {canManage && !locked && (
          <button onClick={() => onAdd(param.id)} title="Assign to layer" style={{
            width: 20, height: 20, borderRadius: radius.sm, border: 'none', padding: 0,
            background: semantic.infoBg, color: semantic.brandNavyMid,
            cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          }}>
            <span className="material-symbols-outlined" style={{ fontSize: 13 }}>chevron_left</span>
          </button>
        )}
      </div>
    </div>
  );
}

function AssignmentDropZone({ side, onDrop, children, label, count, search, onSearch }) {
  const c = useThemeColors();
  const isDark = useIsDark();
  const [over, setOver] = useState(false);

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {/* Column header */}
      <div style={{
        padding: '10px 12px 8px', borderBottom: `1px solid ${c.border}`,
        background: isDark ? 'rgba(255,255,255,0.015)' : semantic.infoBg,
        display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0,
      }}>
        <span className="material-symbols-outlined" style={{
          fontSize: 13,
          color: side === 'assigned' ? (isDark ? semantic.brandAccent : semantic.brandNavyMid) : c.textSubtle,
          fontVariationSettings: side === 'assigned' ? "'FILL' 1" : "'FILL' 0",
        }}>
          {side === 'assigned' ? 'check_circle' : 'radio_button_unchecked'}
        </span>
        <span style={{ fontSize: typography.fontSize.sm, fontWeight: typography.fontWeight.bold, color: c.textSubtle, textTransform: 'uppercase', letterSpacing: typography.letterSpacing.widest, flex: 1 }}>
          {label}
        </span>
        <span style={{
          fontSize: typography.fontSize.xs, fontWeight: typography.fontWeight.bold,
          background: side === 'assigned'
            ? (isDark ? 'rgba(62,181,231,0.12)' : semantic.infoBg)
            : (c.borderSubtle),
          color: side === 'assigned' ? (isDark ? semantic.brandAccent : semantic.brandNavyMid) : c.textSubtle,
          padding: '1px 7px', borderRadius: radius.lg,
        }}>
          {count}
        </span>
      </div>

      {/* Search — available side only */}
      {onSearch !== undefined && (
        <div style={{ padding: '6px 10px', borderBottom: `1px solid ${c.border}`, flexShrink: 0 }}>
          <div style={{ position: 'relative' }}>
            <span className="material-symbols-outlined" style={{ position: 'absolute', left: 7, top: '50%', transform: 'translateY(-50%)', fontSize: 12, color: c.textSubtle, pointerEvents: 'none' }}>search</span>
            <input
              value={search}
              onChange={(e) => onSearch(e.target.value)}
              placeholder="Filter…"
              style={{
                width: '100%', boxSizing: 'border-box',
                padding: '5px 8px 5px 24px', fontSize: typography.fontSize.sm,
                background: c.bgSubtle,
                color: c.textPrimary,
                border: `1px solid ${c.border}`, borderRadius: radius.sm,
                outline: 'none', fontFamily: typography.fontFamily.body,
              }}
            />
          </div>
        </div>
      )}

      {/* Drop area */}
      <div
        onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; setOver(true); }}
        onDragLeave={() => setOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setOver(false);
          const paramId = e.dataTransfer.getData(DND_PARAM);
          if (paramId) onDrop(paramId, side);
        }}
        style={{
          flex: 1, overflowY: 'auto', padding: '8px',
          background: over
            ? (isDark ? 'rgba(62,181,231,0.04)' : 'rgba(30,67,150,0.03)')
            : 'transparent',
          outline: over
            ? `2px dashed ${isDark ? 'rgba(62,181,231,0.35)' : semantic.infoBorder}`
            : '2px solid transparent',
          outlineOffset: '-6px',
          borderRadius: radius.sm,
          transition: 'background 0.15s, outline-color 0.15s',
          minHeight: 80,
        }}
      >
        {children}
      </div>
    </div>
  );
}

function ParameterAssignmentSection({
  layers, parameters, effectiveAssignments,
  onAssign, onUnassign, onDrop: _onDrop,
  canManage, onOpenBulkMapping,
}) {
  const c = useThemeColors();
  const isDark = useIsDark();
  const [selectedId, setSelectedId] = useState(() => layers[0]?.id ?? null);
  const [search, setSearch] = useState('');

  const selectedLayer = layers.find((l) => l.id === selectedId) ?? layers[0] ?? null;

  // Build a map: paramId → layer name it's assigned to (globally)
  const globalAssignment = useMemo(() => {
    const map = {};
    layers.forEach((l) => {
      (effectiveAssignments[l.id] ?? []).forEach((pid) => {
        map[pid] = l.name;
      });
    });
    return map;
  }, [layers, effectiveAssignments]);

  const assignedIds = selectedLayer ? new Set(effectiveAssignments[selectedLayer.id] ?? []) : new Set();
  const assignedParams = parameters.filter((p) => assignedIds.has(p.id));

  const availableFiltered = parameters.filter((p) => {
    if (assignedIds.has(p.id)) return false;
    const q = search.toLowerCase();
    if (!q) return true;
    return p.displayName.toLowerCase().includes(q) || p.dataKey.toLowerCase().includes(q);
  });
  const availableTotal = parameters.filter((p) => !assignedIds.has(p.id));

  function handleDrop(paramId, side) {
    if (!selectedLayer) return;
    if (side === 'assigned') {
      onAssign(selectedLayer.id, paramId);
    } else {
      onUnassign(selectedLayer.id, paramId);
    }
  }

  const dotColor = (layerId) => {
    const idx = layers.findIndex((l) => l.id === layerId);
    return layerColors[idx % layerColors.length];
  };

  return (
    <div style={{
      height: '100%',
      background: c.bg,
      border: `1px solid ${c.border}`,
      borderRadius: radius.xl,
      overflow: 'hidden',
      boxShadow: isDark ? 'none' : '0 2px 8px rgba(30,67,150,0.04)',
      display: 'flex',
      flexDirection: 'column',
    }}>
      {/* Header */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: '8px',
        padding: '12px 16px',
        background: c.bgSubtle,
        borderBottom: `1px solid ${c.border}`,
        flexShrink: 0,
      }}>
        <span className="material-symbols-outlined" style={{ fontSize: 16, color: c.textSubtle, fontVariationSettings: "'FILL' 1" }}>link</span>
        <span style={{ fontSize: typography.fontSize.md, fontWeight: typography.fontWeight.bold, color: c.textPrimary, fontFamily: typography.fontFamily.heading }}>
          Parameter Assignment
        </span>
        {canManage ? (
          <div style={{ marginLeft: 'auto' }}>
            <PrimaryButton variant="secondary" icon="upload_file" size="sm" onClick={onOpenBulkMapping}>
              Upload Mapping
            </PrimaryButton>
          </div>
        ) : null}
      </div>

      <div style={{ display: 'flex', flex: 1, overflow: 'hidden', minHeight: 0 }}>
        {/* Layer selector */}
        <div style={{
          width: 160, flexShrink: 0,
          background: c.bgSubtle,
          borderRight: `1px solid ${c.border}`,
          display: 'flex', flexDirection: 'column',
        }}>
          <div style={{ padding: '8px', flex: 1, overflowY: 'auto' }}>
            {layers.length === 0 && (
              <div style={{ padding: '16px 8px', fontSize: typography.fontSize.sm, color: c.textSubtle, textAlign: 'center' }}>
                No layers yet
              </div>
            )}
            {layers.map((layer, idx) => {
              const dot = layerColors[idx % layerColors.length];
              const active = layer.id === selectedId;
              const count = (effectiveAssignments[layer.id] ?? []).length;
              return (
                <div
                  key={layer.id}
                  onClick={() => { setSelectedId(layer.id); setSearch(''); }}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '8px',
                    padding: '8px 10px', borderRadius: radius.md, marginBottom: '3px',
                    cursor: 'pointer',
                    background: active
                      ? (isDark ? 'rgba(62,181,231,0.1)' : `${dot}12`)
                      : 'transparent',
                    border: active
                      ? `1px solid ${isDark ? 'rgba(62,181,231,0.2)' : `${dot}30`}`
                      : '1px solid transparent',
                    transition: 'all 0.12s',
                  }}
                >
                  <div style={{ width: 8, height: 8, borderRadius: '50%', background: dot, flexShrink: 0 }} />
                  <span style={{ flex: 1, fontSize: typography.fontSize.base, fontWeight: active ? 600 : 400, color: active ? c.textPrimary : c.textSubtle, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {layer.name}
                  </span>
                  {count > 0 && (
                    <span style={{ fontSize: typography.fontSize.xs, fontWeight: typography.fontWeight.bold, background: `${dot}20`, color: dot, padding: '1px 5px', borderRadius: radius.md }}>
                      {count}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Assignment columns */}
        {selectedLayer ? (
          <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
            {/* Assigned */}
            <div style={{ flex: 1, borderRight: `1px solid ${c.border}`, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
              <AssignmentDropZone
                side="assigned"
                label="Assigned"
                count={assignedParams.length}
                onDrop={handleDrop}
              >
                {assignedParams.length === 0 ? (
                  <div style={{ padding: '32px 10px', textAlign: 'center', color: c.borderSubtle, fontSize: typography.fontSize.base, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
                    <span className="material-symbols-outlined" style={{ fontSize: 28, opacity: 0.4 }}>format_list_bulleted_add</span>
                    <span>{canManage ? 'Drag or click ‹ to assign.' : 'None assigned.'}</span>
                  </div>
                ) : assignedParams.map((param) => (
                  <ParamChip
                    key={param.id}
                    param={param}
                    layerColor={dotColor(selectedLayer.id)}
                    onRemove={(pid) => onUnassign(selectedLayer.id, pid)}
                    canManage={canManage}
                  />
                ))}
              </AssignmentDropZone>
            </div>

            {/* Available */}
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
              <AssignmentDropZone
                side="available"
                label="Available"
                count={availableTotal.length}
                search={search}
                onSearch={setSearch}
                onDrop={handleDrop}
              >
                {availableFiltered.length === 0 ? (
                  <div style={{ padding: '32px 10px', textAlign: 'center', color: c.borderSubtle, fontSize: typography.fontSize.base, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
                    <span className="material-symbols-outlined" style={{ fontSize: 28, opacity: 0.4 }}>
                      {search ? 'search_off' : 'check_circle'}
                    </span>
                    <span>{search ? 'No match.' : 'All parameters assigned.'}</span>
                  </div>
                ) : availableFiltered.map((param) => (
                  <AvailableChip
                    key={param.id}
                    param={param}
                    alreadyAssignedTo={globalAssignment[param.id] ?? null}
                    onAdd={(pid) => onAssign(selectedLayer.id, pid)}
                    canManage={canManage}
                  />
                ))}
              </AssignmentDropZone>
            </div>
          </div>
        ) : (
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: c.textSubtle, fontSize: typography.fontSize.md }}>
            Select a layer to assign parameters.
          </div>
        )}
      </div>
    </div>
  );
}

// ── DeleteLayerModal ──────────────────────────────────────────────────────────
function DeleteLayerModal({ layer, onConfirm, onCancel }) {
  const c = useThemeColors();
  if (!layer) return null;
  return (
    <ModalShell
      onClose={onCancel}
      icon="delete"
      iconVariant="danger"
      hideClose
      title="Delete Layer"
      subtitle={
        <>
          Delete <strong style={{ color: c.textPrimary }}>{layer.name}</strong>? Parameter assignments will be lost.
        </>
      }
      maxWidth="340px"
      footer={
        <>
          <button onClick={onCancel} style={{
            padding: '7px 14px', borderRadius: radius.md,
            border: `1px solid ${c.borderInput}`,
            background: 'transparent', color: c.textPrimary,
            fontSize: typography.fontSize.base, fontWeight: typography.fontWeight.medium, cursor: 'pointer',
            fontFamily: typography.fontFamily.body,
          }}>Cancel</button>
          <button onClick={onConfirm} style={{
            padding: '7px 14px', borderRadius: radius.md, border: 'none',
            background: `linear-gradient(135deg, ${semantic.errorAlt}, ${semantic.error})`,
            color: '#ffffff', fontSize: typography.fontSize.base, fontWeight: typography.fontWeight.semibold,
            cursor: 'pointer', fontFamily: typography.fontFamily.body,
          }}>Delete</button>
        </>
      }
    />
  );
}

// ── Main LayersTab ────────────────────────────────────────────────────────────
export function LayersTab({ deploymentId, canManage }) {
  const { data: serverLayers = [], isLoading: layersLoading } = useLayers(deploymentId);
  const { data: parameters = [], isLoading: paramsLoading } = useParameters(deploymentId);
  const toastApiError = useApiErrorToast();
  const createLayer = useCreateLayer(deploymentId);
  const updateLayer = useUpdateLayer(deploymentId);
  const deleteLayer = useDeleteLayer(deploymentId);
  const bulkCreateMapping = useBulkCreateLayerMapping(deploymentId);

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [bulkMappingOpen, setBulkMappingOpen] = useState(false);

  // Local layer order (array of ids) — null means use server order
  const [localOrder, setLocalOrder] = useState(null);

  // Local assignments: layerId → paramId[] (only layers with pending changes)
  const [pendingAssignments, setPendingAssignments] = useState({});
  const [saving, setSaving] = useState(false);

  const hasUnsaved = Object.keys(pendingAssignments).length > 0;


  // Layers in display order
  const layers = useMemo(() => {
    if (localOrder) {
      const map = Object.fromEntries(serverLayers.map((l) => [l.id, l]));
      return localOrder.map((id) => map[id]).filter(Boolean);
    }
    return [...serverLayers].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  }, [serverLayers, localOrder]);

  // Effective assignments merging server state with pending edits
  const effectiveAssignments = useMemo(() => {
    const base = Object.fromEntries(layers.map((l) => [l.id, l.parameterIds ?? []]));
    return { ...base, ...pendingAssignments };
  }, [layers, pendingAssignments]);

  function handleAddLayer() {
    const name = `Layer ${layers.length + 1}`;
    createLayer.mutate({ id: generateId(), name, order: layers.length, parameterIds: [] }, { onError: toastApiError });
  }

  function handleRenameLayer(layer, newName) {
    updateLayer.mutate({ layerId: layer.id, payload: { ...layer, name: newName } }, { onError: toastApiError });
  }

  function handleReorder(srcId, targetId) {
    const ids = layers.map((l) => l.id);
    const srcIdx = ids.indexOf(srcId);
    const tgtIdx = ids.indexOf(targetId);
    if (srcIdx === -1 || tgtIdx === -1) return;
    const next = [...ids];
    next.splice(srcIdx, 1);
    next.splice(tgtIdx, 0, srcId);
    setLocalOrder(next);
    // Persist order immediately
    next.forEach((id, idx) => {
      const layer = layers.find((l) => l.id === id);
      if (layer && layer.order !== idx) {
        updateLayer.mutate({ layerId: id, payload: { ...layer, order: idx } }, { onError: toastApiError });
      }
    });
  }

  function handleAssign(layerId, paramId) {
    // Remove from any other layer first (enforce single-layer rule)
    setPendingAssignments((prev) => {
      const next = { ...prev };
      layers.forEach((l) => {
        if (l.id === layerId) return;
        const current = next[l.id] ?? l.parameterIds ?? [];
        if (current.includes(paramId)) {
          next[l.id] = current.filter((id) => id !== paramId);
        }
      });
      const current = next[layerId] ?? layers.find((l) => l.id === layerId)?.parameterIds ?? [];
      if (!current.includes(paramId)) {
        next[layerId] = [...current, paramId];
      }
      return next;
    });
  }

  function handleUnassign(layerId, paramId) {
    setPendingAssignments((prev) => {
      const current = prev[layerId] ?? layers.find((l) => l.id === layerId)?.parameterIds ?? [];
      return { ...prev, [layerId]: current.filter((id) => id !== paramId) };
    });
  }

  function confirmDelete() {
    if (!deleteTarget) return;
    deleteLayer.mutate(deleteTarget.id, {
      onSuccess: () => {
        setPendingAssignments((prev) => { const n = { ...prev }; delete n[deleteTarget.id]; return n; });
        if (localOrder) setLocalOrder((o) => o?.filter((id) => id !== deleteTarget.id));
        setDeleteTarget(null);
      },
      onError: toastApiError,
    });
  }

  async function handleSave() {
    setSaving(true);
    try {
      await Promise.all(
        Object.entries(pendingAssignments).map(([layerId, parameterIds]) => {
          const layer = layers.find((l) => l.id === layerId);
          if (!layer) return Promise.resolve();
          return updateLayer.mutateAsync({ layerId, payload: { ...layer, parameterIds } });
        })
      );
      setPendingAssignments({});
    } catch (err) {
      toastApiError(err);
    } finally {
      setSaving(false);
    }
  }

  if (layersLoading || paramsLoading) return <SkeletonCard height={460} rows={8} />;

  return (
    <div style={{ fontFamily: typography.fontFamily.body, display: 'flex', flexDirection: 'column', height: '100%' }}>
      <style>{`
        @keyframes fadeSlideIn {
          from { opacity: 0; transform: translateY(-4px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>

      {hasUnsaved && (
        <UnsavedBar label="Unsaved assignment changes" onSave={handleSave} onDiscard={() => setPendingAssignments({})} saving={saving} />
      )}

      {/* Section 1 */}
      <div style={{ flexShrink: 0 }}>
      <LayerManagementSection
        layers={layers}
        onAdd={handleAddLayer}
        onRename={handleRenameLayer}
        onDelete={(layer) => setDeleteTarget(layer)}
        onReorder={handleReorder}
        canManage={canManage}
      />
      </div>

      {/* Section 2 */}
      <div style={{ flex: 1, minHeight: 0 }}>
      <ParameterAssignmentSection
        layers={layers}
        parameters={parameters}
        effectiveAssignments={effectiveAssignments}
        onAssign={handleAssign}
        onUnassign={handleUnassign}
        canManage={canManage}
        onOpenBulkMapping={() => setBulkMappingOpen(true)}
      />
      </div>

      <DeleteLayerModal
        layer={deleteTarget}
        onConfirm={confirmDelete}
        onCancel={() => setDeleteTarget(null)}
      />

      {bulkMappingOpen && (
        <BulkUploadModal
          title="Bulk Upload Layer Mapping"
          subtitle="Map fields to layers in bulk via CSV"
          entityLabel="Mapping"
          requiredColumns={['Field Name', 'Layer Name']}
          previewColumns={[
            { key: 'Field Name', label: 'Field Name', flex: '1fr' },
            { key: 'Layer Name', label: 'Layer Name', flex: '1fr' },
          ]}
          sampleCsv={`Field Name,Layer Name\nTemperature,Raw Material\nPressure,Process`}
          sampleFilename="layer-mapping-sample.csv"
          mutateAsync={(file) => bulkCreateMapping.mutateAsync(file)}
          isPending={bulkCreateMapping.isPending}
          onClose={() => setBulkMappingOpen(false)}
        />
      )}
    </div>
  );
}

function iconBtn(c) {
  return {
    width: 28, height: 28, borderRadius: '7px',
    background: 'transparent', border: `1px solid ${c.borderSubtle}`,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    cursor: 'pointer', padding: 0,
  };
}
